"use client"

import React, { useState } from 'react';
import Dropdown from '../../../../components/DropDown/Dropdown';
import sceneServices from '../../utils/services/scene';
import { useToast } from "../../../../components/Toast/ToastProvider";
import {
  CloseOutlined,
  DescriptionIcon,
  MicrophoneIcon,
  TranslateIcon,
  AlignCenterIcon,
  AlignLeftIcon,
  TagIcon,
  IndentIcon,
  UploadIcon,
  PlayIcon,
  VolumeIcon,
  MoreOptionsIcon,
  CheckIcon,
  ChevronDownIcon,
  TableIcon
} from '../../utils/icons';

interface PopupDescriptionAndExplanationProps {
  title: string; // Title showing the name of the group
  groupId?: string; // Add group ID for API calls
  onClose: () => void;
  onSave: (data: {
    description: {
      vn: string;
      en: string;
      fr: string;
    };
    narration?: File;
  }) => void;
  initialData?: {
    description?: {
      vn?: string;
      en?: string;
      fr?: string;
    };
    narration?: string;
  };
}

const PopupDescriptionAndExplanation: React.FC<PopupDescriptionAndExplanationProps> = ({
  title,
  groupId,
  onClose,
  onSave,
  initialData
}) => {
  const [activeTab, setActiveTab] = useState<'description' | 'narration'>('narration'); // Start with narration tab
  const [activeLanguage, setActiveLanguage] = useState<'vn' | 'en' | 'fr'>('vn');
  const { showError, showSuccess } = useToast();

  const [description, setDescription] = useState({
    vn: initialData?.description?.vn || '',
    en: initialData?.description?.en || '',
    fr: initialData?.description?.fr || '',
  });
    // Narration states
  const [narrationText, setNarrationText] = useState('');
  const [narrationFile, setNarrationFile] = useState<File | null>(null);
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState(''); // New state for manual audio URL input
  const [isSaving, setIsSaving] = useState(false); // Loading state for save action
  
  // Voice options for dropdown
  const femaleVoiceOptions = ['Giọng A', 'Giọng B', 'Giọng C'];
  const maleVoiceOptions = ['Giọng C', 'Giọng D', 'Giọng E'];
  
  // Selected voices
  const [selectedFemaleVoice, setSelectedFemaleVoice] = useState(femaleVoiceOptions[0]);
  const [selectedMaleVoice, setSelectedMaleVoice] = useState(maleVoiceOptions[0]);
  const [selectedGender, setSelectedGender] = useState<'nu' | 'nam'>('nu'); // Female by default

  // Handle description changes
  const handleDescriptionChange = (lang: 'vn' | 'en' | 'fr', value: string) => {
    setDescription(prev => ({
      ...prev,
      [lang]: value
    }));
  };

  // Language change handler
  const handleLanguageChange = (lang: 'vn' | 'en' | 'fr') => {
    setActiveLanguage(lang);
  };
  // Auto translate function (mock implementation)
  const handleAutoTranslate = () => {
    // In a real implementation, this would call a translation API
    if (activeLanguage !== 'vn' && description.vn) {
      // This is a simplified mock translation - in reality you'd call a translation service
      const mockTranslated = `[Translated from Vietnamese] ${description.vn.substring(0, 20)}...`;
      handleDescriptionChange(activeLanguage, mockTranslated);
      showSuccess(`Đã dịch sang ${activeLanguage.toUpperCase()}`, 'Dịch tự động');
    }
  };

  // Generate voice from text
  const handleGenerateVoice = async () => {
    if (!narrationText.trim()) {
      showError('Vui lòng nhập nội dung thuyết minh', 'Lỗi');
      return;
    }

    if (!groupId) {
      showError('Không tìm thấy ID nhóm', 'Lỗi');
      return;
    }

    setIsGeneratingVoice(true);
    
    try {
      // Create mock audio URL - in reality this would be generated from text-to-speech service
      const mockAudioUrl = `https://sanpham.starglobal3d.com/generated_voice_${Date.now()}.mp3`;
      
      // Call API to save voice for group
      const apiBody = {
        language_id: activeLanguage,
        gender: selectedGender,
        audio_path: mockAudioUrl
      };

      const response = await sceneServices.addVoiceForGroup({ 
        body: apiBody, 
        group_id: Number(groupId)
      });

      // Handle Axios response structure
      const res = response as { data?: { success?: boolean; message?: string } } | { success?: boolean; message?: string };
      const apiData = (res as any).data || res;
      if (apiData && apiData.success) {
        setGeneratedAudioUrl(mockAudioUrl);
        setAudioUrl(mockAudioUrl); // Auto-fill the URL input
        showSuccess('Tạo thuyết minh thành công', 'Thành công');
      } else {
        throw new Error(apiData?.message || 'Không thể tạo thuyết minh');
      }

    } catch (error: any) {
      const errorMessage = error.message || 'Có lỗi xảy ra khi tạo thuyết minh';
      showError(errorMessage, 'Lỗi');
    } finally {
      setIsGeneratingVoice(false);
    }
  };

  // Handle narration file upload
  const handleNarrationUpload = (e: React.ChangeEvent<HTMLInputElement>, gender?: 'male' | 'female') => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showError('Kích thước file vượt quá 10MB', 'Lỗi');
        return;
      }
      
      // Store the file with gender info
      setNarrationFile({
        ...file,
        gender, // Store which gender this file is for
      } as File);
        // Log which voice type this file is for
      console.log(`Uploaded audio file for ${gender || 'unknown'} voice`);
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    setIsSaving(true);
    
    try {
      // If we have audio URL and group ID, call the API
      if (audioUrl.trim() && groupId) {
        const apiBody = {
          language_id: activeLanguage,
          gender: selectedGender,
          audio_path: audioUrl.trim()
        };

        const response = await sceneServices.addVoiceForGroup({ 
          body: apiBody, 
          group_id: Number(groupId)
        });

        // Handle Axios response structure
        const apiData = (response as { data?: { success?: boolean; message?: string } } | { success?: boolean; message?: string });
        const data = (apiData as any).data || apiData;

        if (data && data.success) {
          showSuccess('Lưu thuyết minh thành công', 'Thành công');
        } else {
          throw new Error(data?.message || 'Không thể lưu thuyết minh');
        }
      }

      // Call the original onSave callback
      onSave({
        description,
        narration: narrationFile || undefined,
        // We could also send the selected voices as part of the data
        // but we'll keep the interface unchanged for now
      });
      
    } catch (error: any) {
      const errorMessage = error.message || 'Có lỗi xảy ra khi lưu thuyết minh';
      showError(errorMessage, 'Lỗi');
    } finally {
      setIsSaving(false);
    }
    
    // Log selected voices for debugging (can be removed in production)
    console.log('Selected voices:', {
      female: selectedFemaleVoice,
      male: selectedMaleVoice,
      audioUrl: audioUrl
    });
  };
  return (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center z-50 bg-black/30 backdrop-blur-sm">
      <div className="w-[95%] h-[80vh] min-w-[1500px] glass_background rounded-[16px] backdrop-blur-[100px] rounded-2xl shadow-[0px_4px_16px_0px_rgba(0,0,0,0.2),inset_-1px_1px_3px_0px_rgba(255,255,255,0.8),inset_0px_4px_10px_3px_rgba(255,255,255,0.2)] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="p-6 flex flex-row justify-between items-center border-b border-[#ECECEC]">
          <h2 className="text-2xl font-bold uppercase font-['Roboto_Flex']">
            {title}
          </h2>
          
          {/* Close button */}          <button 
            className="w-[60px] h-[60px] bg-white border border-[#ECECEC] rounded-[10px] flex items-center justify-center"
            onClick={onClose}
          >
            <CloseOutlined width="19" height="19" stroke="#464646" strokeWidth="2" />
          </button>
        </div>
        
        {/* Tab Buttons */}
        <div className="flex border-b border-[#ECECEC]">
          <button
            className={`px-8 py-4 flex items-center gap-2 ${
              activeTab === 'description' 
                ? 'border-b-2 border-[#5FCD6F] text-[#5FCD6F]' 
                : 'text-black'
            } transition-all`}
            onClick={() => setActiveTab('description')}
          >            <DescriptionIcon 
              width="24" 
              height="24" 
              stroke={activeTab === 'description' ? "#5FCD6F" : "#000000"} 
              strokeWidth="2" 
            />
            <span className="text-lg font-medium">Thông tin mô tả</span>
          </button>
          
          <button
            className={`px-8 py-4 flex items-center gap-2 ${
              activeTab === 'narration' 
                ? 'border-b-2 border-[#5FCD6F] text-[#5FCD6F]' 
                : 'text-black'
            } transition-all`}            onClick={() => setActiveTab('narration')}
          >
            <MicrophoneIcon 
              width="24" 
              height="24" 
              stroke={activeTab === 'narration' ? "#5FCD6F" : "#000000"} 
            />
            <span className="text-lg font-medium">Thuyết minh</span>
          </button>
        </div>
        
        {/* Tab Content */}
        <div className="p-8 flex-1 overflow-y-auto">
          {activeTab === 'description' ? (
            <div className="flex flex-col gap-8">
              {/* Language Selector */}
              <div className="flex items-center gap-4">
                <button
                  className={`px-5 py-3 ${activeLanguage === 'vn' ? 'bg-gray-200' : 'bg-white'} rounded-lg font-medium text-black`}
                  onClick={() => handleLanguageChange('vn')}
                >
                  VN {activeLanguage === 'vn' && '(mặc định)'}
                </button>
                <button
                  className={`px-5 py-3 ${activeLanguage === 'en' ? 'bg-gray-200' : 'bg-white'} rounded-lg font-medium text-black`}
                  onClick={() => handleLanguageChange('en')}
                >
                  EN
                </button>
                <button
                  className={`px-5 py-3 ${activeLanguage === 'fr' ? 'bg-gray-200' : 'bg-white'} rounded-lg font-medium text-black`}
                  onClick={() => handleLanguageChange('fr')}
                >
                  FR
                </button>
              </div>
              
              {/* Auto-translate Button */}
              {activeLanguage !== 'vn' && (
                <button
                  className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-gray-200 w-fit hover:bg-gray-50"                  onClick={handleAutoTranslate}
                >
                  <TranslateIcon width="24" height="24" />
                  <span className="text-base">Dịch tự động từ ngôn ngữ mặc định</span>
                </button>
              )}
              
              {/* Rich Text Editor */}
              <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
                {/* Toolbar */}
                <div className="flex items-center gap-1 p-2 border-b border-gray-200">
                  <div className="flex items-center gap-1 mr-2">                    <button className="p-1.5 hover:bg-gray-100 rounded">
                      <span className="inline-block w-[100px] overflow-hidden truncate text-left text-gray-700">Đoạn văn</span>
                      <ChevronDownIcon className="inline-block ml-1" width="10" height="6" />
                    </button>
                  </div>
                  
                  <div className="h-6 w-px bg-gray-300 mx-1"></div>
                    <button className="p-1.5 hover:bg-gray-100 rounded">
                    <TranslateIcon width="20" height="20" />
                  </button>
                  
                  <button className="p-1.5 hover:bg-gray-100 rounded font-bold">
                    B
                  </button>
                  
                  <button className="p-1.5 hover:bg-gray-100 rounded italic">
                    I
                  </button>
                  
                  <button className="p-1.5 hover:bg-gray-100 rounded underline">
                    U
                  </button>
                  
                  <div className="h-6 w-px bg-gray-300 mx-1"></div>
                    <button className="p-1.5 hover:bg-gray-100 rounded">
                    <AlignCenterIcon width="20" height="20" />
                  </button>
                    <button className="p-1.5 hover:bg-gray-100 rounded">
                    <AlignLeftIcon width="20" height="20" />
                  </button>
                  
                  <div className="h-6 w-px bg-gray-300 mx-1"></div>
                    <button className="p-1.5 hover:bg-gray-100 rounded">
                    <TagIcon width="20" height="20" />
                  </button>
                    <button className="p-1.5 hover:bg-gray-100 rounded">
                    <IndentIcon width="20" height="20" />
                  </button>
                  
                  <div className="h-6 w-px bg-gray-300 mx-1"></div>
                    <button className="p-1.5 hover:bg-gray-100 rounded">
                    <span className="text-sm">📋</span>
                  </button>                  <button className="p-1.5 hover:bg-gray-100 rounded">
                    <TableIcon width="20" height="20" />
                  </button>
                    <button className="p-1.5 hover:bg-gray-100 rounded">
                    <span className="text-sm">📹</span>
                  </button>
                </div>
                
                {/* Editor */}
                <div className="p-4 min-h-[300px]">
                  <textarea
                    className="w-full h-full border-0 outline-none resize-none min-h-[290px]"
                    value={description[activeLanguage]}
                    onChange={(e) => handleDescriptionChange(activeLanguage, e.target.value)}
                    placeholder={`Nhập mô tả nội dung tại đây (${activeLanguage.toUpperCase()})...`}
                  ></textarea>
                </div>
              </div>
            </div>
          ) : (            <div className="flex flex-col gap-6">
              {/* Language Selector (same as description tab) */}
              <div className="flex items-center gap-4">
                <button
                  className={`px-5 py-3 ${activeLanguage === 'vn' ? 'bg-gray-200' : 'bg-white'} rounded-lg font-medium text-black`}
                  onClick={() => handleLanguageChange('vn')}
                >
                  VN {activeLanguage === 'vn' && '(mặc định)'}
                </button>
                <button
                  className={`px-5 py-3 ${activeLanguage === 'en' ? 'bg-gray-200' : 'bg-white'} rounded-lg font-medium text-black`}
                  onClick={() => handleLanguageChange('en')}
                >
                  EN
                </button>
                <button
                  className={`px-5 py-3 ${activeLanguage === 'fr' ? 'bg-gray-200' : 'bg-white'} rounded-lg font-medium text-black`}
                  onClick={() => handleLanguageChange('fr')}
                >
                  FR
                </button>
              </div>              {/* Auto-translate Button (same as description tab) */}
              {activeLanguage !== 'vn' && (
                <button
                  className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-gray-200 w-fit hover:bg-gray-50"
                  onClick={handleAutoTranslate}
                >
                  <TranslateIcon width="24" height="24" />
                  <span className="text-base">Dịch tự động từ ngôn ngữ mặc định</span>
                </button>
              )}{/* Narration Script */}
              <div className="flex flex-col gap-2 w-full">
                <span className="text-xl font-bold">Kịch bản thuyết minh:</span>
                <div className="w-full h-[230px] bg-white border border-[#E0E0E0] rounded-[6px] p-4">
                  <textarea 
                    className="w-full h-full resize-none outline-none"
                    value={narrationText}
                    onChange={(e) => setNarrationText(e.target.value)}
                    placeholder={`Nhập kịch bản thuyết minh tại đây (${activeLanguage.toUpperCase()})...`}
                  ></textarea>
                </div>
              </div>

              {/* Voice Selection */}
              <div className="flex flex-col gap-4">
                <span className="text-xl font-bold">Chọn giọng đọc:</span>
                
                {/* Gender Selection */}
                <div className="flex gap-4 items-center">
                  <span className="text-lg">Giới tính:</span>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="nu"
                        checked={selectedGender === 'nu'}
                        onChange={(e) => setSelectedGender(e.target.value as 'nu' | 'nam')}
                        className="w-4 h-4"
                      />
                      <span>Nữ</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="nam"
                        checked={selectedGender === 'nam'}
                        onChange={(e) => setSelectedGender(e.target.value as 'nu' | 'nam')}
                        className="w-4 h-4"
                      />
                      <span>Nam</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 w-full">                  {/* Female Voice */}
                  <div className="flex-1 flex flex-col gap-2">
                    <span className="text-xl">Giọng đọc nữ:</span>
                    <Dropdown 
                      options={femaleVoiceOptions} 
                      value={selectedFemaleVoice} 
                      onChange={setSelectedFemaleVoice}
                      placeholder="Chọn giọng đọc nữ"
                    />
                  </div>
                  
                  {/* Male Voice */}
                  <div className="flex-1 flex flex-col gap-2">
                    <span className="text-xl">Giọng đọc nam:</span>
                    <Dropdown 
                      options={maleVoiceOptions} 
                      value={selectedMaleVoice} 
                      onChange={setSelectedMaleVoice}
                      placeholder="Chọn giọng đọc nam"
                    />
                  </div>
                </div>
              </div>              {/* Auto-Generate Narration Button */}
              <button
                className={`flex items-center gap-2 px-4 py-3 border rounded-[10px] w-fit transition-colors ${
                  isGeneratingVoice 
                    ? 'bg-gray-200 border-gray-300 cursor-not-allowed' 
                    : 'bg-[#5FCD6F] border-[#5FCD6F] text-white hover:bg-[#4cbc5c]'
                }`}
                onClick={handleGenerateVoice}
                disabled={isGeneratingVoice}
              >
                {isGeneratingVoice ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-base">Đang tạo thuyết minh...</span>
                  </>                ) : (
                  <>
                    <MicrophoneIcon width="24" height="24" />
                    <span className="text-base">Tạo thuyết minh tự động</span>                  </>
                )}
              </button>              {/* Results Section */}
              <div className="flex flex-col gap-4 mt-4">
                <span className="text-xl font-bold">Kết quả: (Tự động được tạo hoặc tải lên thuyết minh có sẵn)</span>
                
                {/* Manual Audio URL Input */}
                <div className="flex flex-col gap-2">
                  <span className="text-lg font-bold">URL Audio thuyết minh:</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={audioUrl}
                      onChange={(e) => setAudioUrl(e.target.value)}
                      placeholder="Nhập URL audio thuyết minh (vd: https://sanpham.starglobal3d.com/audio.mp3)"
                      className="flex-1 h-10 border border-[#E0E0E0] rounded-md px-3 outline-none"
                    />
                    {audioUrl && (
                      <audio controls className="h-10">
                        <source src={audioUrl} type="audio/mpeg" />
                        Trình duyệt không hỗ trợ audio.
                      </audio>
                    )}
                  </div>
                </div>

                {/* Generated Audio Result */}
                {generatedAudioUrl && (
                  <div className="flex flex-col gap-2">
                    <span className="text-lg">File audio đã tạo tự động:</span>
                    <div className="flex items-center w-full h-[40px] bg-white border border-[#E0E0E0] rounded-[6px] px-4">
                      <span className="text-base truncate flex-grow">{generatedAudioUrl}</span>
                      <button 
                        className="ml-2 px-3 py-1 bg-[#5FCD6F] text-white text-sm rounded hover:bg-[#4cbc5c]"
                        onClick={() => setAudioUrl(generatedAudioUrl)}
                      >
                        Sử dụng
                      </button>
                      <audio controls className="ml-4">
                        <source src={generatedAudioUrl} type="audio/mpeg" />
                        Trình duyệt không hỗ trợ audio.
                      </audio>
                    </div>
                  </div>
                )}

                {/* Female Audio URL */}
                <div className="flex flex-col gap-2">
                  <span className="text-xl">Audio URL Nữ: {selectedFemaleVoice}</span>
                  <div className="flex items-center w-full h-[40px] bg-white border border-[#E0E0E0] rounded-[6px] px-4">
                    <span className="text-xl truncate flex-grow">{narrationFile ? narrationFile.name : `/upload/audios/text-to-speech/2024-08-23/${selectedFemaleVoice.replace(/\s+/g, '')}_Nu_${activeLanguage}.mp3`}</span>
                    <div className="relative w-10 h-10 bg-white flex items-center justify-center cursor-pointer">
                      <input
                        type="file"
                        id="femaleAudioUpload"
                        accept="audio/*"
                        className="absolute inset-0 opacity-0 cursor-pointer"                        onChange={(e) => handleNarrationUpload(e, 'female')}
                      />
                      <UploadIcon width="24" height="24" />
                    </div>
                  </div>
                    {/* Audio Player - Female */}
                  <div className="flex items-center w-full h-[60px] bg-white border border-[#E0E0E0] rounded-[100px] px-6">                    {/* Play Button */}
                    <button className="w-10 h-10 rounded-full flex items-center justify-center">
                      <PlayIcon width="24" height="24" style={{ fill: "#656565" }} />
                    </button>
                    
                    {/* Divider */}
                    <div className="w-[4px] h-[18px] bg-[#D9D9D9] mx-3"></div>
                    
                    {/* Time */}
                    <span className="text-xl text-[#656565] mr-3">00:01/05:01</span>
                    
                    {/* Progress Bar */}
                    <div className="flex-grow h-[4px] bg-[#B0B0B0] rounded-full overflow-hidden">
                      <div className="w-[20%] h-full bg-[#656565]"></div>
                    </div>
                      {/* Volume Icon */}
                    <VolumeIcon width="24" height="24" className="ml-3" style={{ fill: "#656565", stroke: "#656565" }} />
                      {/* Options */}
                    <MoreOptionsIcon width="24" height="24" className="ml-3" />
                  </div>
                </div>
                  {/* Male Audio URL */}
                <div className="flex flex-col gap-2">
                  <span className="text-xl">Audio URL Nam: {selectedMaleVoice}</span>
                  <div className="flex items-center w-full h-[40px] bg-white border border-[#E0E0E0] rounded-[6px] px-4">
                    <span className="text-xl truncate flex-grow">{`/upload/audios/text-to-speech/2024-08-23/${selectedMaleVoice.replace(/\s+/g, '')}_Nam_${activeLanguage}.mp3`}</span>
                    <div className="relative w-10 h-10 bg-white flex items-center justify-center cursor-pointer">                      <input
                        type="file"
                        id="maleAudioUpload"
                        accept="audio/*"
                        className="absolute inset-0 opacity-0 cursor-pointer"                        onChange={(e) => handleNarrationUpload(e, 'male')}
                      />
                      <UploadIcon width="24" height="24" />
                    </div>
                  </div>
                    {/* Audio Player - Male (identical to female player) */}
                  <div className="flex items-center w-full h-[60px] bg-white border border-[#E0E0E0] rounded-[100px] px-6">
                    <button className="w-10 h-10 rounded-full flex items-center justify-center">
                      <PlayIcon width="24" height="24" style={{ fill: "#656565" }} />
                    </button>
                    <div className="w-[4px] h-[18px] bg-[#D9D9D9] mx-3"></div>
                    <span className="text-xl text-[#656565] mr-3">00:01/05:01</span>
                    <div className="flex-grow h-[4px] bg-[#B0B0B0] rounded-full overflow-hidden">
                      <div className="w-[20%] h-full bg-[#656565]"></div>                    </div>
                    <VolumeIcon width="24" height="24" className="ml-3" style={{ fill: "#656565", stroke: "#656565" }} />
                    <MoreOptionsIcon width="24" height="24" className="ml-3" />
                  </div>
                </div>
              </div>
                {/* Auto-play Option */}
              <div className="flex items-center gap-4 py-6">
                <div className="w-6 h-6 bg-[#00A417] rounded flex items-center justify-center">
                  <CheckIcon width="12" height="10" stroke="white" />
                </div>
                <span className="text-lg">Tự động phát thuyết minh theo cảnh</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer/Action Buttons */}
        <div className="p-6 border-t border-[#ECECEC] bg-white/60">
          <div className="flex gap-4">
            <button
              className="flex-1 h-12 bg-white border border-[#E0E0E0] text-black rounded-[10px] flex items-center justify-center hover:bg-[#F6F6F6] transition"
              onClick={onClose}
            >
              <span className="text-lg font-medium">Hủy bỏ</span>
            </button>            <button
              className={`flex-1 h-12 ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#5FCD6F] hover:bg-[#4cbc5c]'} text-white rounded-[10px] flex items-center justify-center gap-2 transition`}
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-lg font-medium">Đang lưu...</span>
                </>              ) : (
                <>
                  <CheckIcon width="24" height="24" fill="white" />
                  <span className="text-lg font-medium">Lưu & cập nhật</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupDescriptionAndExplanation;
